import {
  Button,
  CircularProgress,
  Divider,
  ListItem,
  Menu,
  Paper,
  Slider,
  styled,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  toggleButtonGroupClasses,
  Tooltip,
  Typography,
  // IconButton,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CancelIcon from "@mui/icons-material/Cancel";
import FormatColorFillIcon from "@mui/icons-material/FormatColorFill";
import React, { ChangeEvent, RefObject, useContext, useState } from "react";
import { dimension } from "./DrawingContainer";
import { ReactSketchCanvasRef } from "react-sketch-canvas";
import EraserIcon from "./icons/EraserIcon";
import TuneIcon from "@mui/icons-material/Tune";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SaveIcon from "@mui/icons-material/Save";
import usePopup from "../../../hooks/usePopup";
import { EmrContext } from "../..";
import { useMutation } from "react-query";
import { postActivity } from "../../services";
import { useParams } from "react-router-dom";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import { DrawingContainerContext } from ".";

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  [`& .${toggleButtonGroupClasses.grouped}`]: {
    margin: theme.spacing(0.5),
    border: 0,
    borderRadius: theme.shape.borderRadius,
    [`&.${toggleButtonGroupClasses.disabled}`]: {
      border: 0,
    },
  },
  [`& .${toggleButtonGroupClasses.middleButton},& .${toggleButtonGroupClasses.lastButton}`]:
    {
      marginLeft: -1,
      borderLeft: "1px solid transparent",
    },
}));
function ButtonGroup() {
  const targetWidth = dimension.width;
  const targetHeight = dimension.height;
  // const targetHeight = dimension.height - 120;
  const { CustomModal, showSnackbar } = usePopup();
  const { encounterId } = useParams();
  const { patientData } = useContext(EmrContext);
  const [alignment, setAlignment] = useState("Draw");
  const [formats, setFormats] = useState("");
  const [resultImage, setResultImage] = useState("");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openColor, setOpenColor] = useState<null | HTMLElement>(null);
  const [openLink, setOpeLink] = useState<null | HTMLElement>(null);
  const [show, setShow] = useState(false);
  const open = Boolean(anchorEl);
  const chooseColor = Boolean(openColor);
  const linkOpen = Boolean(openLink);
  const {
    canvasRef,
    setEraseMode,
    setStrokeWidth,
    setEraserWidth,
    eraserWidth,
    strokeWidth,
    setOperation,
    strokeColor,
    setStrokeColor,
    setImageUrl,
    imageUrl,
    setSearchParams,
    imageDetails,
    setImageDetails
  } = useContext(DrawingContainerContext);

  const { mutate: saveActivity, isLoading: saveLoading } = useMutation(
    postActivity,
    {
      onSuccess: (result) => {
        if (result.responseIndicator == "success") {
          showSnackbar({
            message: result.responseMessage,
            open: true,
            variant: "success",
            duration: 2000,
          });
          setSearchParams((prev) => ({
            ...prev,
            refetch: true,
          }));
          setImageDetails(null)
          handleClearClick();
        } else {
          showSnackbar({
            message: result.responseMessage,
            open: true,
            variant: "warning",
            duration: 2000,
          });
        }
      },
    }
  );

  const handleFormat = (
    _: React.MouseEvent<HTMLElement>,
    newFormats: string
  ) => {
    setFormats(newFormats);
  };

  const handleAlignment = (
    _: React.MouseEvent<HTMLElement>,
    newAlignment: string
  ) => {
    setAlignment(newAlignment);
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleOpenColor = (event: React.MouseEvent<HTMLElement>) => {
    setOpenColor(event.currentTarget);
  };

  const handleOpenLink = (event: React.MouseEvent<HTMLElement>) => {
    setOpeLink(event.currentTarget);
  };

  const handleCloseLink = () => {
    setOpeLink(null);
  };

  const handleCloseColor = () => {
    setOpenColor(null);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handlePenClick = () => {
    setOperation("cursor-crosshair");
    setEraseMode(false);
    (canvasRef as RefObject<ReactSketchCanvasRef>).current?.eraseMode(false);
  };

  const handleEraserClick = () => {
    setOperation("cursor-cell");
    setEraseMode(true);
    (canvasRef as RefObject<ReactSketchCanvasRef>).current?.eraseMode(true);
  };

  const handleColorChange = (e: ChangeEvent<HTMLInputElement>) => {
    setStrokeColor(e.target.value);
  };

  const handleStrokeWidthChange = (_: Event, newValue: number | number[]) => {
    setStrokeWidth(newValue as number);
  };

  const handleEraserWidthChange = (_: Event, newValue: number | number[]) => {
    setEraserWidth(newValue as number);
  };

  const handleUndoClick = () => {
    (canvasRef as RefObject<ReactSketchCanvasRef>).current?.undo();
  };

  const handleRedoClick = () => {
    (canvasRef as RefObject<ReactSketchCanvasRef>).current?.redo();
  };

  const handleClearClick = () => {
    setImageUrl("");
    (canvasRef as RefObject<ReactSketchCanvasRef>).current?.clearCanvas();
  };

  const handleResetClick = () => {
    (canvasRef as RefObject<ReactSketchCanvasRef>).current?.resetCanvas();
  };

  const handleImageResizeFromUrl = (url: string) => {
    const img = new Image();
    img.crossOrigin = "anonymous"; // Allow cross-origin requests

    img.src = url;

    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = targetWidth;
      canvas.height = targetHeight;

      if (ctx) {
        ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const resizedUrl = URL.createObjectURL(blob);
              setImageUrl(resizedUrl); // Assuming setResizedImage is a state setter
            }
          },
          "image/jpeg",
          0.8 // Quality (0-1)
        );
      }
    };

    img.onerror = () => {
      console.error("Failed to load image from URL.");
    };
  };

  const handleImageResizeFromFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.crossOrigin = "anonymous"; // Allow cross-origin requests for local files if needed
      img.src = reader.result as string;

      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        canvas.width = targetWidth;
        canvas.height = targetHeight;

        if (ctx) {
          ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
          canvas.toBlob(
            (blob) => {
              if (blob) {
                const resizedUrl = URL.createObjectURL(blob);
                setImageUrl(resizedUrl); // Assuming setImageUrl is a state setter
              }
            },
            "image/jpeg",
            0.8 // Quality (0-1)
          );
        }
      };

      img.onerror = () => {
        console.error("Failed to load image.");
      };
    };
    reader.onerror = () => {
      console.error("Failed to read file.");
    };
    reader.readAsDataURL(file);
  };

  const handleSelectFileUrl = (url: string) => {
    handleImageResizeFromUrl(url);
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (files) {
      // const objectUrl = URL.createObjectURL(files[0]); // Create a temporary object URL
      // handleImageResizeFromUrl(objectUrl);
      handleImageResizeFromFile(files[0]);
    }
  };

  const handleSave = async () => {
    const fullBodyFile = (await canvasRef?.current?.exportImage(
      "png"
    )) as string;
    const randomNumber = Math.floor(Math.random() * 10000);
    let imgName = `${patientData.pmaster.patientName}${randomNumber}.png`
    if(Boolean(imageDetails)){
      setResultImage(fullBodyFile);
      if (imageUrl) {
        saveActivity({
          activityImageId: imageDetails?.activityImageId as number,
          fullBodyFile,
          imgName: imageDetails?.imgName as string || imgName,
          encounterId: parseInt(encounterId as string),
        });
      }
    }else{
      setResultImage(fullBodyFile);
      if (imageUrl) {
        saveActivity({
          activityImageId: null,
          fullBodyFile,
          imgName,
          encounterId: parseInt(encounterId as string),
        });
      } else {
        showSnackbar({
          message: "Please select an image",
          open: true,
          variant: "warning",
          duration: 2000,
        });
      }
    }
  };

  const handleView = async () => {
    const fullBodyFile = (await canvasRef?.current?.exportImage(
      "png"
    )) as string;
    setResultImage(fullBodyFile);
    setShow(true);
  };

  return (
    <div className="flex ">
      <CustomModal title="Preview" open={show} setOpen={setShow}>
        <div className="w-[40vw] h-[60dvh] flex justify-center items-center flex-col">
          <img src={resultImage} alt="image" className="border-2" />
        </div>
      </CustomModal>
      <Paper
        elevation={0}
        sx={(theme) => ({
          display: "flex",
          border: `1px solid ${theme.palette.divider}`,
          flexWrap: "wrap",
          width: "fit-content",
        })}
      >
        <StyledToggleButtonGroup
          size="small"
          value={alignment}
          exclusive
          onChange={handleAlignment}
          aria-label="text alignment"
        >
          <Tooltip title="Preview">
            <ToggleButton
              value="Preview"
              aria-label="Preview"
              onClick={handleView}
            >
              <RemoveRedEyeIcon color="secondary" />
            </ToggleButton>
          </Tooltip>
          <Tooltip title="Draw">
            <ToggleButton
              value="Draw"
              aria-label="Draw"
              onClick={handlePenClick}
            >
              <EditIcon />
            </ToggleButton>
          </Tooltip>
          <Tooltip title="Erase">
            <ToggleButton
              value="Erase"
              aria-label="Erase"
              onClick={handleEraserClick}
            >
              <EraserIcon />
            </ToggleButton>
          </Tooltip>

          <Tooltip title="Adjust">
            <ToggleButton
              value="Adjust"
              aria-label="Adjust"
              onClick={handleClick}
            >
              <TuneIcon />
            </ToggleButton>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            id="account-menu"
            open={open}
            onClose={handleClose}
            slotProps={{
              paper: {
                elevation: 0,
                sx: {
                  overflow: "visible",
                  filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                  mt: 1.5,
                  "& .MuiAvatar-root": {
                    width: 32,
                    height: 32,
                    ml: -0.5,
                    mr: 1,
                  },
                  "&::before": {
                    content: '""',
                    display: "block",
                    position: "absolute",
                    top: 0,
                    left: 14,
                    width: 10,
                    height: 10,
                    bgcolor: "background.paper",
                    transform: "translateY(-50%) rotate(45deg)",
                    zIndex: 0,
                  },
                },
              },
            }}
            transformOrigin={{ horizontal: "left", vertical: "top" }}
            anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
          >
            <ListItem>
              <div className="w-[20vw] flex flex-col">
                <Typography gutterBottom>Pen width</Typography>
                <Slider
                  size="small"
                  defaultValue={5}
                  value={strokeWidth}
                  onChange={handleStrokeWidthChange}
                  aria-label="Small"
                  valueLabelDisplay="auto"
                />
              </div>
            </ListItem>
            <Divider />
            <ListItem>
              <div className="w-[20vw] flex flex-col">
                <Typography gutterBottom>Eraser width</Typography>
                <Slider
                  size="small"
                  defaultValue={10}
                  value={eraserWidth}
                  onChange={handleEraserWidthChange}
                  aria-label="Small"
                  valueLabelDisplay="auto"
                />
              </div>
            </ListItem>
          </Menu>
          <Tooltip title="Color">
            <ToggleButton
              value="color"
              aria-label="color"
              onClick={handleOpenColor}
            >
              <FormatColorFillIcon sx={{ color: strokeColor }} />
            </ToggleButton>
          </Tooltip>
          <Menu
            anchorEl={openColor}
            id="account-menu"
            open={chooseColor}
            onClose={handleCloseColor}
            slotProps={{
              paper: {
                elevation: 0,
                sx: {
                  overflow: "visible",
                  filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                  mt: 1.5,
                  "& .MuiAvatar-root": {
                    width: 32,
                    height: 32,
                    ml: -0.5,
                    mr: 1,
                  },
                  "&::before": {
                    content: '""',
                    display: "block",
                    position: "absolute",
                    top: 0,
                    left: 14,
                    width: 10,
                    height: 10,
                    bgcolor: "background.paper",
                    transform: "translateY(-50%) rotate(45deg)",
                    zIndex: 0,
                  },
                },
              },
            }}
            transformOrigin={{ horizontal: "left", vertical: "top" }}
            anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
          >
            <ListItem>
              <div className="w-[20vw] flex flex-col">
                <TextField
                  type="color"
                  value={strokeColor}
                  onChange={handleColorChange}
                />
              </div>
            </ListItem>
          </Menu>
        </StyledToggleButtonGroup>

        <Divider flexItem orientation="vertical" sx={{ mx: 0.5, my: 1 }} />

        <StyledToggleButtonGroup
          size="small"
          exclusive
          value={formats}
          onChange={handleFormat}
          aria-label="text formatting"
        >
          <Tooltip title="Undo">
            <ToggleButton
              value="bold"
              aria-label="Undo"
              onClick={handleUndoClick}
            >
              <UndoIcon />
            </ToggleButton>
          </Tooltip>
          <Tooltip title="Redo">
            <ToggleButton
              value="italic"
              aria-label="Redo"
              onClick={handleRedoClick}
            >
              <RedoIcon />
            </ToggleButton>
          </Tooltip>
          <Tooltip title="Reset">
            <ToggleButton
              value="underlined"
              aria-label="underlined"
              onClick={handleResetClick}
            >
              <RestartAltIcon />
            </ToggleButton>
          </Tooltip>
          <Tooltip title="Clear">
            <ToggleButton
              value="color"
              aria-label="color"
              onClick={handleClearClick}
            >
              <CancelIcon color="error" />
            </ToggleButton>
          </Tooltip>
        </StyledToggleButtonGroup>
        <StyledToggleButtonGroup>
          <Divider flexItem orientation="vertical" sx={{ mx: 0.5, my: 1 }} />
          <Tooltip title="Attach">
            <ToggleButton
              value="attachfile"
              aria-label="attachfile"
              onClick={handleOpenLink}
            >
              <AttachFileIcon color="primary" />
            </ToggleButton>
          </Tooltip>
          <Menu
            anchorEl={openLink}
            id="account-menu"
            open={linkOpen}
            onClose={handleCloseLink}
            slotProps={{
              paper: {
                elevation: 0,
                sx: {
                  overflow: "visible",
                  filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                  mt: 1.5,
                  // "& .MuiAvatar-root": {
                  //   width: 32,
                  //   height: 32,
                  //   // ml: -0.5,
                  //   // mr: 1,
                  // },
                  // "&::before": {
                  //   content: '""',
                  //   display: "block",
                  //   position: "absolute",
                  //   top: 0,
                  //   left: "10%",
                  //   width: 10,
                  //   height: 10,
                  //   bgcolor: "background.paper",
                  //   transform: "translateY(-50%) rotate(45deg)",
                  //   zIndex: 0,
                  // },
                },
              },
            }}
            transformOrigin={{ horizontal: "left", vertical: "top" }}
            anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
          >
            <ListItem>
              <div className="w-[30vw] h-[10dvh] flex flex-col gap-3">
                <TextField
                  value={imageUrl}
                  onChange={(e) => {
                    handleSelectFileUrl(e.target.value);
                  }}
                />

                <div className="flex justify-end items-end">
                  <input
                    type="file"
                    id="fileInput"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                  <Button component="label" htmlFor="fileInput">
                    Browse
                  </Button>
                </div>
              </div>
            </ListItem>
          </Menu>
        </StyledToggleButtonGroup>
        <StyledToggleButtonGroup>
          <Divider flexItem orientation="vertical" sx={{ mx: 0.5, my: 1 }} />
          <Tooltip title="Save">
            <ToggleButton
              value="attachfile"
              aria-label="attachfile"
              onClick={handleSave}
              disabled={saveLoading}
            >
              {saveLoading ? (
                <CircularProgress size={18} />
              ) : (
                <SaveIcon color="success" />
              )}
            </ToggleButton>
          </Tooltip>
        </StyledToggleButtonGroup>
      </Paper>
    </div>
  );
}

export default ButtonGroup;
